import React from "react";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";
import TopBarOverview from "@/ui/molecules/TopBarOverview";

import type { TChartsOfAccountsTab } from "@/ui/router/routerPaths";
import ChartsOfAccountsList from "@/ui/organisms/ChartsOfAccountsList";
import AdminPanel from "@/ui/organisms/AdminPanel";
import type { UserDatabase } from "@/ui/organisms/userTypes";
import type {
    TChartOfAccountsDefinition,
    TCompany,
    TNewCoADefinition,
} from "@/apis/odin/apiODIN";
import { Button } from "@proda-ai/ui-kit";
export const chartsOfAccountsDashboardTabs = [
    {
        value: "list",
        label: "List",
    },
    {
        value: "admin",
        label: "Admin",
    },
] as const;
export type ViewChartsOfAccountsDashboardParams = {
    useState: () => {
        coas:
            | {
                  coaId: number;
                  name: string;
                  deals?: number;
                  lastEdited?: string;
                  lastEditedBy?: string;
                  type?: string;
                  isDraft: boolean;
              }[]
            | null;
        adminDatabases: Record<string, UserDatabase[]>;
        coasRequestStatus: "idle" | "pending" | "rejected" | "resolved";
        getAdminDatabasesState: "idle" | "pending" | "rejected" | "resolved";
        availableCoAsForDatabaseId: TChartOfAccountsDefinition[];
        dashboardSelectedDatabaseIds: number[];
        availableCoAsForDatabaseIdState:
            | "idle"
            | "pending"
            | "rejected"
            | "resolved";
        userDataState: "idle" | "pending" | "rejected" | "resolved";
        dashboardSelectedCoAIds: number[];
        isGlobalAdmin: boolean;
        createCoASelectedDatabases: number[];
        companies: TCompany[] | null;
        getCompaniesState: "idle" | "pending" | "rejected" | "resolved";
        isCreateDraftCoaPending: boolean;
        isCreateDraftCoaError: boolean;
        isProdaUser: boolean;
    };
    useCases: () => {
        onMount: () => void;
        onUnmount: () => void;
        navigateToCoaDetails: (coaId: number) => void;
        navigateToCoaEditDetails: (coaId: number) => void;
        navigateToCreateCoA: () => void;
        handleSubmitAddAccountPermissions: () => Promise<void>;
        handleSubmitDeleteAccountPermissions: () => Promise<void>;
        setDashboardSelectedDatabaseIds: (databaseIds: number[]) => void;
        setDashboardSelectedCoAIds: (coaIds: number[]) => void;
        onAdminMount: () => void;
        onAdminUnmount: () => void;
        getAllAvailableCoAsForUser: () => Promise<void>;
        handleSubmitCreateCoA: (data: TNewCoADefinition) => Promise<void>;
        setCreateCoASelectedDatabases: (databaseIds: number[]) => void;
    };
};

export type ViewChartsOfAccountsDashboardProps = BoxProps & {
    activeTab: TChartsOfAccountsTab;
    onClickBackButton: () => void;
    setActiveTab: (tab: TChartsOfAccountsTab) => void;
};

export const createViewChartsOfAccountsDashboard = ({
    useState,
    useCases,
}: ViewChartsOfAccountsDashboardParams) => {
    const ViewChartsOfAccountsDashboard: React.FC<
        ViewChartsOfAccountsDashboardProps
    > = props => {
        const {
            onClickBackButton,
            setActiveTab,
            activeTab,
            sx,
            ...otherProps
        } = props;

        const {
            coas,
            coasRequestStatus,
            adminDatabases,
            dashboardSelectedDatabaseIds,
            availableCoAsForDatabaseIdState,
            availableCoAsForDatabaseId,
            userDataState,
            dashboardSelectedCoAIds,
            getAdminDatabasesState,
            isGlobalAdmin,
            isProdaUser,
        } = useState();

        const {
            onMount,
            onUnmount,
            navigateToCoaDetails,
            navigateToCoaEditDetails,
            navigateToCreateCoA,
            handleSubmitAddAccountPermissions,
            handleSubmitDeleteAccountPermissions,
            setDashboardSelectedDatabaseIds,
            setDashboardSelectedCoAIds,
            onAdminMount,
            onAdminUnmount,
        } = useCases();

        React.useEffect(() => {
            onMount();
            return onUnmount;
        }, [onMount, onUnmount]);

        return (
            <Box
                data-testid="ViewChartsOfAccountsDashboard"
                sx={{
                    width: "100%",
                    height: "100%",
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column",
                    ...sx,
                }}
                {...otherProps}
            >
                <TopBarOverview
                    title="Charts of Accounts"
                    backButtonLabel="Back to Projects"
                    onBackButtonClick={onClickBackButton}
                    sx={{
                        marginBottom: "10px",
                    }}
                />
                <Box
                    sx={{
                        borderBottom: "1px solid var(--proda-white-130)",
                        minHeight: "50px",
                        height: "50px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        px: "1.5rem",
                        boxSizing: "border-box",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            height: "100%",
                            gap: "0.8rem",
                            alignItems: "center",
                        }}
                    >
                        {chartsOfAccountsDashboardTabs
                            .filter(
                                tab => tab.value !== "admin" || isGlobalAdmin,
                            )
                            .map(({ value, label }) => (
                                <Box
                                    key={value}
                                    component="button"
                                    onClick={() => setActiveTab(value)}
                                    sx={{
                                        p: "0 0 0.8rem 0",
                                        border: "none",
                                        borderBottom: "2px solid",
                                        borderColor:
                                            activeTab === value
                                                ? "var(--primary-color)"
                                                : "transparent",
                                        backgroundColor: "transparent",
                                        cursor: "pointer",
                                        color:
                                            activeTab === value
                                                ? "var(--primary-color)"
                                                : "inherit",
                                        fontWeight:
                                            activeTab === value
                                                ? "bold"
                                                : "normal",
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                            color: "var(--primary-color)",
                                        },
                                        height: "100%",
                                        boxSizing: "border-box",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    {label}
                                </Box>
                            ))}
                    </Box>
                    {activeTab === "list" && isProdaUser && (
                        <Box
                            sx={{
                                display: "flex",
                                gap: "0.8rem",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                width: "100%",
                            }}
                        >
                            <Button
                                onClick={navigateToCreateCoA}
                                size="medium"
                                isInline={true}
                                leadingIcon="plus"
                            >
                                Create Chart of Accounts
                            </Button>
                        </Box>
                    )}
                </Box>
                {activeTab === "list" && (
                    <ChartsOfAccountsList
                        coasRequestStatus={coasRequestStatus}
                        coas={coas}
                        onClickOnCoa={navigateToCoaDetails}
                        onClickOnDraftCoa={navigateToCoaEditDetails}
                    />
                )}
                {activeTab === "admin" && isGlobalAdmin && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            marginTop: "80px",
                        }}
                    >
                        <AdminPanel
                            availableCoAsForDatabaseIdState={
                                availableCoAsForDatabaseIdState
                            }
                            coasRequestStatus={coasRequestStatus}
                            getAdminDatabasesState={getAdminDatabasesState}
                            availableCoAsForDatabaseId={
                                availableCoAsForDatabaseId
                            }
                            handleSubmitAddAccountPermissions={
                                handleSubmitAddAccountPermissions
                            }
                            handleSubmitDeleteAccountPermissions={
                                handleSubmitDeleteAccountPermissions
                            }
                            adminDatabases={adminDatabases}
                            dashboardSelectedDatabaseIds={
                                dashboardSelectedDatabaseIds
                            }
                            setDashboardSelectedDatabaseIds={
                                setDashboardSelectedDatabaseIds
                            }
                            dashboardSelectedCoAIds={dashboardSelectedCoAIds}
                            setDashboardSelectedCoAIds={
                                setDashboardSelectedCoAIds
                            }
                            userDataState={userDataState}
                            onAdminMount={onAdminMount}
                            onAdminUnmount={onAdminUnmount}
                        />
                    </Box>
                )}
            </Box>
        );
    };

    return ViewChartsOfAccountsDashboard;
};
